import api from './api';
import { AxiosError } from 'axios';

export enum RSVPStatus {
  ATTENDING = 'attending',
  DECLINED = 'declined',
  PENDING = 'pending',
  TENTATIVE = 'tentative',
}

export enum MealChoice {
  NOT_SELECTED = 'not_selected',
  VEGETARIAN = 'vegetarian',
  VEGAN = 'vegan',
  FISH = 'fish',
  CHICKEN = 'chicken',
  BEEF = 'beef',
  SPECIAL_REQUEST = 'special_request',
}

export interface Guest {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  attendanceStatus: RSVPStatus;
  plusOneAllowed?: boolean;
  hasPlusOne?: boolean;
  plusOneGuest?: string;
  mealChoice?: string;
  specialMealRequest?: string;
  notes?: string;
  couple: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GuestCounts {
  total: number;
  attending: number;
  declined: number;
  pending: number;
  tentative: number;
}

// Interface for the public guest lookup response
export interface PublicGuestResponse {
  found: boolean;
  guest: {
    _id: string;
    name: string;
    email: string;
    rsvpStatus: string;
    hasResponded: boolean;
    notes?: string;
    specialMealRequest?: string;
    plusOneAllowed?: boolean;
  } | null;
  error?: string;
}

const GuestService = {
  async getGuests(): Promise<Guest[]> {
    try {
      const response = await api.get<Guest[]>('/guests');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  async getGuestCounts(): Promise<GuestCounts> {
    try {
      const response = await api.get<GuestCounts>('/guests/counts');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  async createGuest(guestData: Partial<Guest>): Promise<Guest> {
    try {
      // Make sure the couple ID is included - it should be coming from the component
      if (!guestData.couple) {
        throw new Error('Couple ID is required when creating a guest');
      }
       
      const response = await api.post<Guest>('/guests', guestData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  async updateGuest(id: string, guestData: Partial<Guest>): Promise<Guest> {
    try {
      // For updates, we don't send the couple ID as it's already associated with the guest
      // and the backend will use the authenticated couple's ID from the token
      const response = await api.patch<Guest>(`/guests/${id}`, guestData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  async deleteGuest(id: string): Promise<void> {
    try {
      await api.delete(`/guests/${id}`);
    } catch (error) {
      throw this.handleError(error);
    }
  },

  async createRSVP(rsvpData: any): Promise<any> {
    try {
      const response = await api.post(`/guests/response`, rsvpData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  // Public RSVP methods - these don't require authentication
  
  /**
   * Look up a guest by email for the public RSVP form
   * This doesn't require authentication and uses the public endpoint
   * @param email Guest email address
   * @param coupleId Couple ID
   * @returns PublicGuestResponse with guest info or null if not found
   */
  async findGuestByEmail(email: string, coupleId: string): Promise<PublicGuestResponse> {
    try {
      console.log(`Searching for guest with email: ${email} and couple: ${coupleId}`);
      const response = await api.get<PublicGuestResponse>(
        `/guests/public-search?email=${encodeURIComponent(email)}&couple=${coupleId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error looking up guest:', error);
      return {
        found: false,
        guest: null,
        error: 'Failed to look up guest'
      };
    }
  },

  /**
   * Submit an RSVP response
   * This doesn't require authentication and uses the public endpoint
   * @param rsvpData RSVP submission data 
   * @returns The created or updated guest record
   */
  async submitRSVP(rsvpData: any): Promise<Guest> {
    try {
      // Make sure the lastName property is not empty
      if (!rsvpData.lastName || rsvpData.lastName.trim() === '') {
        // Use 'Guest' as a default lastName if none provided
        rsvpData.lastName = 'Guest';
      }
      
      // If still using the old format with 'name', convert it to firstName and lastName
      if (rsvpData.name && !rsvpData.firstName) {
        const nameParts = rsvpData.name.split(' ');
        rsvpData.firstName = nameParts[0];
        rsvpData.lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : 'Guest';
      }
      
      console.log('Submitting RSVP with data:', rsvpData);
      const response = await api.post<Guest>('/guests/rsvp', rsvpData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  },

  handleError(error: any): Error {
    console.error('Guest service error:', error);
    
    if (error.response && error.response.data) {
      // Handle NestJS validation errors
      if (error.response.data.message && Array.isArray(error.response.data.message)) {
        return new Error(error.response.data.message.join(', '));
      }
      
      // Handle simple error message
      if (error.response.data.message) {
        return new Error(error.response.data.message);
      }
    }
    
    // Default error handling
    return error instanceof Error ? error : new Error('An unexpected error occurred');
  }
}

export default GuestService;